import React from 'react';

// Filled -> outline icon mapping for the pedigree sidebar refresh
// (PEDIGREE_SIDEBAR_REFRESH). Swapping filled section icons for their outline
// variant is the single biggest step in quietening the icon column. Every icon
// used by both sidebar trees was audited and each has an `...Outlined` variant
// in the installed @mui/icons-material, so there are no filled-only exceptions.
//
// The map is keyed by the FILLED component reference. `item.icon` in util.js is
// a pre-built element (e.g. `<DashboardIcon fontSize="medium" />`); its `.type`
// is the same module default export we import here, so referential equality
// holds and the lookup succeeds. Remove this module once the flag is retired.
import DashboardIcon from '@mui/icons-material/Dashboard';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import LabelIcon from '@mui/icons-material/Label';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import NoteIcon from '@mui/icons-material/Note';
import NoteOutlinedIcon from '@mui/icons-material/NoteOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import GetAppIcon from '@mui/icons-material/GetApp';
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import PersonIcon from '@mui/icons-material/Person';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import BallotIcon from '@mui/icons-material/Ballot';
import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined';
import BlurLinearIcon from '@mui/icons-material/BlurLinear';
import BlurLinearOutlinedIcon from '@mui/icons-material/BlurLinearOutlined';
import AssistantIcon from '@mui/icons-material/Assistant';
import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import HistoryIcon from '@mui/icons-material/History';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';

const OUTLINE_BY_FILLED = new Map([
  [DashboardIcon, DashboardOutlinedIcon],
  [LabelIcon, LabelOutlinedIcon],
  [LocalOfferIcon, LocalOfferOutlinedIcon],
  [CollectionsBookmarkIcon, CollectionsBookmarkOutlinedIcon],
  [NoteIcon, NoteOutlinedIcon],
  [DescriptionIcon, DescriptionOutlinedIcon],
  [InsightsIcon, InsightsOutlinedIcon],
  [AltRouteIcon, AltRouteOutlinedIcon],
  [AccountTreeIcon, AccountTreeOutlinedIcon],
  [GetAppIcon, GetAppOutlinedIcon],
  [PersonAddIcon, PersonAddOutlinedIcon],
  [LaunchIcon, LaunchOutlinedIcon],
  [PersonIcon, PersonOutlinedIcon],
  [AssignmentIcon, AssignmentOutlinedIcon],
  [LocalHospitalIcon, LocalHospitalOutlinedIcon],
  [AssignmentLateIcon, AssignmentLateOutlinedIcon],
  [BallotIcon, BallotOutlinedIcon],
  [BlurLinearIcon, BlurLinearOutlinedIcon],
  [AssistantIcon, AssistantOutlinedIcon],
  [LinkIcon, LinkOutlinedIcon],
  [VerifiedUserIcon, VerifiedUserOutlinedIcon],
  [HistoryIcon, HistoryOutlinedIcon]
]);

// Return the outline equivalent of a filled section-icon element, preserving
// its props (e.g. fontSize). Falls back to the original element when no outline
// mapping exists, so an unmapped icon degrades to the filled glyph (recoloured
// to the mid-grey token by CSS) rather than disappearing.
export const toOutlineIcon = (element) => {
  if (!React.isValidElement(element)) return element;
  const Outlined = OUTLINE_BY_FILLED.get(element.type);
  if (!Outlined) return element;
  return React.createElement(Outlined, element.props);
};

export default OUTLINE_BY_FILLED;
